import { BondScreenerFormValues } from '@oneview/components/~advisor/Insights/Screeners/BondScreener/schema';
import { FundScreenerFormValues } from '@oneview/components/~advisor/Insights/Screeners/FundScreener/schema';
import { StockScreenerFormValues } from '@oneview/components/~advisor/Insights/Screeners/StockScreener/schema';
import { createQueryFn } from '@oneview/redux/utils';
import {
    InsightOrdering,
    InsightOrderingDirectionEnum,
    InsightGetTopFundsRequestLeverageEnum,
    InsightGetTopFundsRequestFundTypeEnum,
    InsightGetTopFundsRequestInverseEnum,
} from '@orbis-systems/orbis-api-clients/insight';
import {
    EtfBreakdownCategory,
    FundScreenerValues,
    GlobalGroupCode,
    IndustryType,
    IpoDataSortOption,
    IpoPerformanceRange,
    TopFundsOrderField,
} from '@orbis-systems/orbis-api-middleware';
import { api } from './baseApi';

export interface OptionData {
    underlyingSymbol: string;
    expiration: string;
    callVolume: number;
    putVolume: number;
    totalVolume: number;
    callOi: number;
    putOi: number;
    callVolAvg30Day: number;
    callOiAvg30Day: number;
    putVolAvg30Day: number;
    putOiAvg30Day: number;
    callVolCallOiRatio: number;
    putVolPutOiRatio: number;
    callOiPutOiRatio: number;
    putOiCallOiRatio: number;
    callVolPutVolRatio: number;
    putVolCallVolRatio: number;
    callVol30DayAvgRatio: number;
    putVol30DayAvgRatio: number;
    callOi30DayAvgRatio: number;
    putOi30DayAvgRatio: number;
    updated: string;
    symbol: string;
    option: boolean;
    bond: boolean;
}

export interface OptionsDataBySymbol {
    [symbol: string]: OptionData[];
}

export const researchApi = api.injectEndpoints({
    endpoints: (build) => ({
        getEtfDetails: build.query({
            queryFn: createQueryFn(async (symbol: string, { extra: api }) =>
                api.orbisMiddleware.research.getEtfDetails(symbol),
            ),
        }),
        getUpcomingEarnings: build.query({
            queryFn: createQueryFn(({ days, symbols }: { days?: number; symbols?: string[] }, { extra: api }) =>
                api.orbisMiddleware.research.getUpcomingEarnings({ symbols, days, paging: { limit: 50, offset: 0 } }),
            ),
        }),
        getIndustrySectors: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) =>
                api.orbisMiddleware.research.getSectorsRanking(IndustryType.Group),
            ),
        }),
        getTopTenCompanies: build.query({
            queryFn: createQueryFn((industryCode: GlobalGroupCode, { extra: api }) =>
                api.orbisMiddleware.research.getTopTenCompanies(industryCode),
            ),
        }),
        getSectorTopEtfs: build.query({
            queryFn: createQueryFn((industryCode: GlobalGroupCode, { extra: api }) =>
                api.orbisMiddleware.research.getTopTenEtfsBySector(industryCode),
            ),
        }),
        getTopTenEtfs: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) =>
                api.orbisMiddleware.research.getTopFunds({
                    fund_type: InsightGetTopFundsRequestFundTypeEnum.ETF,
                    inverse: InsightGetTopFundsRequestInverseEnum.ANY,
                    leverage: InsightGetTopFundsRequestLeverageEnum.ANY,
                    paging: { limit: 10, offset: 0 },
                }),
            ),
        }),
        getTopTenEtfsByType: build.query({
            queryFn: createQueryFn(({ dataset }: { dataset: EtfBreakdownCategory }, { extra: api }) => {
                function getOrderOptions(): InsightOrdering {
                    switch (dataset) {
                        case EtfBreakdownCategory.TopVolume:
                            return {
                                direction: InsightOrderingDirectionEnum.Desc,
                                field: TopFundsOrderField.Volume,
                            };
                        case EtfBreakdownCategory.TopValue:
                            return {
                                direction: InsightOrderingDirectionEnum.Desc,
                                field: TopFundsOrderField.DollarVolume,
                            };
                        case EtfBreakdownCategory.TopChange:
                            return {
                                direction: InsightOrderingDirectionEnum.Desc,
                                field: TopFundsOrderField.PriceChangePercent,
                            };
                        case EtfBreakdownCategory.BottomChange:
                            return {
                                direction: InsightOrderingDirectionEnum.Asc,
                                field: TopFundsOrderField.PriceChangePercent,
                            };
                    }
                }

                return api.orbisMiddleware.research.getTopFunds({
                    fund_type: InsightGetTopFundsRequestFundTypeEnum.ETF,
                    inverse: InsightGetTopFundsRequestInverseEnum.ANY,
                    leverage: InsightGetTopFundsRequestLeverageEnum.ANY,
                    paging: {
                        limit: 10,
                        offset: 0,
                    },
                    order: getOrderOptions(),
                });
            }),
        }),
        getEtfInitCommonParams: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.research.getEtfInitCommonParams()),
        }),
        getIpoRecent: build.query({
            queryFn: createQueryFn((sort: IpoDataSortOption, { extra: api }) =>
                api.orbisMiddleware.research.getIpoRecent(
                    { limit: 10, offset: 0 },
                    { direction: InsightOrderingDirectionEnum.Desc, field: sort },
                    true,
                    IpoPerformanceRange.OneYear,
                ),
            ),
        }),
        getIpoUpcoming: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) =>
                api.orbisMiddleware.research.getIpoUpcoming({ limit: 10, offset: 0 }),
            ),
        }),
        getIpoPerformance: build.query({
            queryFn: createQueryFn(
                (
                    {
                        time,
                    }: {
                        time?: IpoPerformanceRange;
                    },
                    { extra: api },
                ) => api.orbisMiddleware.research.getIpoPerformance({ limit: 10, offset: 0 }, true, time),
            ),
        }),
        getBondScreener: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        preset,
                        pagination: { page, size },
                    }: {
                        preset: Partial<BondScreenerFormValues>;
                        pagination: {
                            page: number;
                            size: number;
                        };
                    },
                    { extra: api },
                ) => api.orbisMiddleware.research.getBondScreener(preset, { page, size }),
            ),
        }),

        getBondScreenerPresets: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        limit,
                        offset,
                    }: {
                        limit: number;
                        offset: number;
                    },
                    { extra: api },
                ) => api.orbisMiddleware.research.getBondScreenerPresets(limit, offset),
            ),
        }),
        getFundScreener: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        values,
                        paging: { limit, offset },
                    }: {
                        values: Partial<FundScreenerFormValues>;
                        paging: {
                            limit: number;
                            offset: number;
                        };
                    },
                    { extra: api },
                ) =>
                    api.orbisMiddleware.research.getFundScreener(values as Partial<FundScreenerValues>, {
                        limit,
                        offset,
                    }),
            ),
        }),
        getFundScreenerFilters: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) =>
                api.orbisMiddleware.research.getFundScreenerFilters(),
            ),
        }),
        getStockScreener: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        preset,
                        pagination: { page, size },
                    }: {
                        preset: Partial<StockScreenerFormValues>;
                        pagination: {
                            page: number;
                            size: number;
                        };
                    },
                    { extra: api },
                ) => api.orbisMiddleware.research.getStockScreener(preset, { page, size }),
            ),
        }),
        getOptionRatios: build.query({
            queryFn: createQueryFn(async (symbols: string[], { extra: api }) => {
                const response = await api.orbisSdk.get<OptionsDataBySymbol>(
                    `research/option/ratios`,
                    {},
                    { params: { symbols: symbols.join(',') } },
                );
                return response.data;
            }),
        }),
    }),
});

export const {
    useGetEtfDetailsQuery,
    useGetUpcomingEarningsQuery,
    useGetIndustrySectorsQuery,
    useGetTopTenCompaniesQuery,
    useGetSectorTopEtfsQuery,
    useGetTopTenEtfsQuery,
    useGetTopTenEtfsByTypeQuery,
    useGetEtfInitCommonParamsQuery,
    useGetIpoRecentQuery,
    useGetIpoUpcomingQuery,
    useGetIpoPerformanceQuery,
    useGetBondScreenerMutation,
    useGetBondScreenerPresetsQuery,
    useGetFundScreenerFiltersQuery,
    useGetFundScreenerMutation,
    useGetStockScreenerMutation,
    useGetOptionRatiosQuery,
} = researchApi;
